import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { Modal } from '../../components/Modal';
import { newIdempotencyKey } from '../../lib/api';
import { useSession } from '../../lib/useSession';
import { describeApiError } from '../qrs/errors';
import { useApiQuery } from '../qrs/hooks';
import { permissionsFor } from '../qrs/permissions';
import { QrTable } from '../qrs/QrTable';
import { exclusionText, formatDate, formatNumber, STATE_LABEL, STATE_ORDER } from '../qrs/states';
import type { Lot, PrintedResult } from '../qrs/types';
import { useMembers } from '../qrs/useMemberNames';
import { useOrgApi } from '../qrs/useOrgApi';
import { useQrList } from '../qrs/useQrList';
import { stripLogoUrl } from '../style/binaryApi';
import { describeStyle, LOCAL_PRESETS } from '../style/presets';
import { StyleEditor } from '../style/StyleEditor';
import type { QrStyle, StyleIssue } from '../style/types';
import { AssignCustodyModal } from './AssignCustodyModal';
import { KitModal } from './KitModal';
import { lotStatus } from './lotUtils';

export function LotDetailPage() {
  const { lotId = '' } = useParams();
  const client = useOrgApi();
  const { activeOrg } = useSession();
  const perms = permissionsFor(activeOrg?.role);
  const lot = useApiQuery((signal) => client.get<Lot>(`/v1/lots/${encodeURIComponent(lotId)}`, { signal }), [client, lotId]);
  const slots = useQrList(client, { lotId });
  const { names } = useMembers(client, perms.manageLots);
  const [modal, setModal] = useState<'custody' | 'kit' | 'printed' | 'style' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftStyle, setDraftStyle] = useState<QrStyle | null>(null);
  const [styleIssues, setStyleIssues] = useState<StyleIssue[]>([]);

  if (lot.error) {
    return (
      <section className="space-y-4">
        <Link to="/consola/lotes" className="text-sm text-green underline">← Lotes</Link>
        <Banner tone="error" action={<Button size="sm" variant="secondary" onClick={lot.reload}>Reintentar</Button>}>
          {describeApiError(lot.error, 'No pudimos cargar el lote.')}
        </Banner>
      </section>
    );
  }
  const data = lot.data;
  const counts = data?.counts ?? {};
  const generated = counts.generado ?? 0;

  async function markPrinted() {
    if (!data) return;
    setBusy(true);
    try {
      const res = await client.post<PrintedResult>(`/v1/lots/${encodeURIComponent(data.id)}/printed`, undefined, { idempotencyKey: newIdempotencyKey() });
      const updated = res.printed ?? 0;
      const excluded = res.excluded ?? [];
      setNotice({ tone: 'success', text: `${formatNumber(updated)} QR pasaron a impreso.${excluded.length ? ` Excluidos: ${excluded.map(exclusionText).join(' · ')}.` : ''}` });
      lot.reload();
      slots.reload();
    } catch (err) {
      setNotice({ tone: 'error', text: describeApiError(err, 'No pudimos marcar el lote como impreso.') });
    } finally {
      setBusy(false);
      setModal(null);
    }
  }

  async function saveStyle() {
    if (!data || !draftStyle) return;
    setBusy(true);
    try {
      const res = await client.patch<{ warning?: { code?: string } }>(`/v1/lots/${encodeURIComponent(data.id)}/style`, { qrStyle: stripLogoUrl(draftStyle) });
      const printed = Boolean(res?.warning) || data.status === 'impreso';
      setNotice({
        tone: printed ? 'warning' : 'success',
        text: printed ? 'Estilo guardado para los próximos kits. Las calcomanías ya impresas no cambian.' : 'Estilo del lote guardado.',
      });
      setModal(null);
      lot.reload();
    } catch (err) {
      setNotice({ tone: 'error', text: describeApiError(err, 'No pudimos guardar el estilo.') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-5">
      <Link to="/consola/lotes" className="text-sm text-green underline hover:text-ink">
        ← Lotes
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">
            Lote <span className="font-mono">{lotId}</span>
          </h1>
          <p className="text-sm text-ink-muted">
            {data ? `${data.name ? `${data.name} · ` : ''}${formatNumber(data.quantity)} QR · creado ${formatDate(data.createdAt)}` : 'Cargando…'}
          </p>
        </div>
        {data && <Chip tone={lotStatus(data).tone}>{lotStatus(data).label}</Chip>}
      </div>

      {notice && (
        <Banner tone={notice.tone} action={<button type="button" className="text-xs underline" onClick={() => setNotice(null)}>Cerrar</button>}>
          {notice.text}
        </Banner>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          <ul className="grid grid-cols-3 gap-2 text-center" aria-label="Distribución del lote">
            {STATE_ORDER.map((s) => (
              <li key={s} className="rounded-md border border-line bg-white p-2">
                <div className="text-xs text-ink-muted">{s === 'impreso' ? 'Sin asignar' : STATE_LABEL[s]}</div>
                <div className="font-semibold tabular-nums">{lot.loading ? '…' : formatNumber(counts[s] ?? 0)}</div>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3 rounded-lg border border-line bg-white p-3">
            <div className="flex-1">
              <div className="text-xs text-ink-muted">Estilo del lote</div>
              <div className="font-medium">{describeStyle(data?.qrStyle)}</div>
              <div className="text-xs text-ink-muted">{data?.qrStyle ? 'Se imprime como imagen de 300 dpi dentro del PDF' : 'PDF vectorial'}</div>
            </div>
            {perms.manageLots && (
              <Button size="sm" variant="secondary" onClick={() => { setDraftStyle(data?.qrStyle ?? LOCAL_PRESETS[0].qrStyle); setModal('style'); }}>
                Cambiar estilo
              </Button>
            )}
          </div>
          {perms.manageLots && (
            <div className="space-y-2">
              <Button block variant="secondary" onClick={() => setModal('kit')} disabled={!data}>
                Descargar kit de impresión
              </Button>
              <Button block variant="secondary" onClick={() => setModal('printed')} disabled={!data || generated === 0}>
                {generated === 0 ? 'Marcar lote como impreso (nada pendiente)' : `Marcar ${formatNumber(generated)} QR como impresos`}
              </Button>
              <Button block onClick={() => setModal('custody')} disabled={!data}>
                Asignar custodia
              </Button>
              <p className="text-xs text-ink-muted">
                Al marcar impreso, solo los QR en <b>generado</b> pasan a <b>impreso</b>; los dados de baja conservan su estado.
              </p>
            </div>
          )}
        </div>
        <div className="space-y-2 lg:col-span-2">
          <h2 className="font-display text-base font-bold text-ink">QR del lote</h2>
          {slots.error ? (
            <Banner tone="error">{describeApiError(slots.error, 'No pudimos cargar los QR del lote.')}</Banner>
          ) : (
            <QrTable caption="QR del lote" loading={slots.loading} rows={slots.data?.items ?? []} columns={['code', 'state', 'custodian', 'business', 'actions']} names={names} empty="El lote no tiene QR visibles." />
          )}
        </div>
      </div>

      {data && modal === 'custody' && <AssignCustodyModal lot={data} open onClose={() => setModal(null)} onDone={() => { lot.reload(); slots.reload(); }} />}
      {data && <KitModal open={modal === 'kit'} onClose={() => setModal(null)} lotId={data.id} lotLabel={data.name ? `${data.id} · ${data.name}` : data.id} style={data.qrStyle} />}
      <Modal
        open={modal === 'printed'}
        onClose={() => setModal(null)}
        title="Marcar lote como impreso"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => void markPrinted()} loading={busy}>Marcar impresos</Button>
          </>
        }
      >
        <p className="text-sm">
          {formatNumber(generated)} QR pasan de <b>generado</b> a <b>impreso</b> y quedan listos para asignar custodia.
        </p>
      </Modal>
      <Modal
        open={modal === 'style'}
        onClose={() => setModal(null)}
        title="Estilo del lote"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancelar</Button>
            <Button onClick={() => void saveStyle()} loading={busy} disabled={styleIssues.length > 0}>Guardar estilo</Button>
          </>
        }
      >
        {data?.status === 'impreso' && <Banner tone="warning">Este lote ya está impreso: el estilo nuevo solo aplica a los próximos kits.</Banner>}
        {draftStyle && <StyleEditor value={draftStyle} onChange={setDraftStyle} orgId={client.orgId} onIssues={setStyleIssues} />}
      </Modal>
    </section>
  );
}
